/** Stable i18n keys thrown by authClient — each app maps them in its own locale files. */
export const AuthErrorKey = {
  LoginFailed: "LoginFailed",
  FetchUserDataFailed: "FetchUserDataFailed",
  InvalidCredentials: "InvalidCredentials",
  EmailNotConfirmed: "EmailNotConfirmed",
  UserDeactivated: "UserDeactivated",
  TokenNoLongerValid: "TokenNoLongerValid",
  SignInNotAllowed: "SignInNotAllowed",
  TokenRefreshFailed: "TokenRefreshFailed",
  RegistrationFailed: "RegistrationFailed",
  EmailAlreadyExists: "EmailAlreadyExists",
  CaptchaRequired: "CaptchaRequired",
  CaptchaFailed: "CaptchaFailed",
  ResendConfirmationFailed: "ResendConfirmationFailed",
} as const;

export type AuthErrorKeyName = (typeof AuthErrorKey)[keyof typeof AuthErrorKey];

export type AuthErrorBody = {
  error?: string;
  error_description?: string;
};

function normalize(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Maps auth-server `error_description` values from `/connect/token` and `/Account/Register`.
 * Source: auth.gastrosoft.de AuthorizationController + AccountController.
 */
export function mapAuthErrorDescription(
  description: string,
  fallback: AuthErrorKeyName = AuthErrorKey.LoginFailed,
): AuthErrorKeyName {
  const desc = description.trim();
  if (!desc) return fallback;

  const n = normalize(desc);

  if (n === "email is not confirmed to log in.") {
    return AuthErrorKey.EmailNotConfirmed;
  }
  if (n.includes("username/password couple is invalid")) {
    return AuthErrorKey.InvalidCredentials;
  }
  if (n === "user has been deactivated") {
    return AuthErrorKey.UserDeactivated;
  }
  if (n === "the token is no longer valid.") {
    return AuthErrorKey.TokenNoLongerValid;
  }
  if (n === "the user is no longer allowed to sign in.") {
    return AuthErrorKey.SignInNotAllowed;
  }
  if (n.includes("already exist")) {
    return AuthErrorKey.EmailAlreadyExists;
  }
  if (n.includes("captcha verification required")) {
    return AuthErrorKey.CaptchaRequired;
  }
  if (n.includes("captcha verification failed")) {
    return AuthErrorKey.CaptchaFailed;
  }

  return fallback;
}

export function mapAuthServerError(
  body: AuthErrorBody | undefined,
  fallback: AuthErrorKeyName = AuthErrorKey.LoginFailed,
): AuthErrorKeyName {
  const description = body?.error_description?.trim() ?? "";
  if (description) {
    return mapAuthErrorDescription(description, fallback);
  }

  if (body?.error?.trim() === "invalid_grant") {
    return AuthErrorKey.InvalidCredentials;
  }

  return fallback;
}
